import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { ComponentDefinition, TagDateValue, TagDefinition, ConnectionPoint, DigitalSet, TagDefinitionDTO, VariableType, VariableSubType, Dictionary } from '../../../../models';
import { ILoggerService, ItagStatisticsService, IDateRangeService, IAuthorizationService, IStoreService, ISaveMessagesService } from '../../../../services';
import { momentFormat, isEditMode } from '../../../../common';
import { manualInputRoles } from '../../../../common/Roles';
import { IRootScope } from 'IRootScope';
import { Store, StoreElement } from '../../../../stateManagement';
import { Unsubscribe } from 'redux';

class TagsStatisticsStateController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    data: TagDateValue[];
    selectedTag: TagDefinition;
    searchString: string;
    initialRange: any;
    hasManualInputRole: boolean;
    currentInterval: any;
    currentBeginDate: string;
    currentEndDate: string;
    hideHeaders = false;
    mode: string;
    isEditMode: boolean;
    formController: angular.IFormController;
    // From store
    connectionPoints: ConnectionPoint[];
    digitalSets: DigitalSet[];
    tags: TagDefinition[];
    tagsDto: TagDefinitionDTO[];
    variableTypesDictionary: Dictionary<VariableType>;
    variableSubTypesDictionary: Dictionary<VariableSubType>;

    private _guidToReselect: string;
    private _unsubscribe: Unsubscribe;

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        'tagStatisticsService',
        'dateRangeService',
        'authorizationService',
        'loggerService',
        'storeService',
        'saveMessagesService'
    ];

    constructor(private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _tagStatisticsService: ItagStatisticsService,
        private _dateRangeService: IDateRangeService,
        authorizationService: IAuthorizationService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService,
        private _saveMessagesService: ISaveMessagesService
    ) {
        this.hasManualInputRole = authorizationService.isInRoles(manualInputRoles);
    }

    $onInit() {
        this.hideHeaders = this._$state.params.hideHeaders;
        if (this._$state.params.beginDate && this._$state.params.endDate) {
            this.initialRange = {
                beginDate: moment(this._$state.params.beginDate).format(momentFormat),
                endDate: moment(this._$state.params.endDate).format(momentFormat)
            };
        }
        const tag = this._$state.params['tag'];
        if (tag) {
            this.reselectTag(tag);
        } else if (this._$state.params.tagDefinitionGuid) {
            this._guidToReselect = this._$state.params.tagDefinitionGuid;
        }
        this.isEditMode = this._$rootScope.isEditMode;

        this._$scope.$on(isEditMode, (event, data) => {
            this.isEditMode = data;
        });

        if (this._$state.params.mode) {
            this.mode = this._$state.params.mode;
        } else {
            this.mode = '';
        }

        this._$scope.$watchGroup([() => this.currentBeginDate, () => this.currentEndDate], () => {
            this.currentInterval = { startDate: this.currentBeginDate, endDate: this.currentEndDate };
        });

        this._unsubscribe = this._storeService.register(
            store => this.updateStore(store),
            [
                StoreElement.TagsDto,
                StoreElement.Tags,
                StoreElement.ConnectionPoints,
                StoreElement.DigitalSets,
                StoreElement.Variables
            ]
        );
    }

    $onDestroy() {
        this._unsubscribe();
    }

    updateStore(store: Store) {
        this.connectionPoints = _.values<ConnectionPoint>(store.connectionPoints.dictionary);
        this.digitalSets = _.values<DigitalSet>(store.digitalSets.dictionary);
        this.tags = _.values<TagDefinition>(store.tags.dictionary);
        this.tagsDto = _.values<TagDefinitionDTO>(store.tagsDto.dictionary);
        this.variableTypesDictionary = store.variablesTypes.dictionary;
        this.variableSubTypesDictionary = store.variableSubTypes.dictionary;

        // try to reselect once the tag on loading if any in parameters
        if (this._guidToReselect) {
            const tag = store.tags.dictionary[this._guidToReselect];
            if (tag) {
                this.reselectTag(tag);
            }
            this._guidToReselect = undefined;
        }
    }

    autoFit() {
        if (this.data && this.data.length > 0) {
            this._tagStatisticsService
                .autofit([this.selectedTag.guid], this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string)
                .then(data => {
                    this.selectedTag.min = data.minimum;
                    this.selectedTag.max = data.maximum;
                    this.selectedTag.stdDevThreshold = data.stdDevThreshold;
                    this.formController.$setDirty();
                    this._loggerService.logSuccess('Statistics successfully fitted.');
                });
        } else {
            this._loggerService.logWarning('No data to fit statistics on.', true);
        }
    }

    isStandAlone(): boolean {
        return this._$state.current.name === 'tagsStatistics';
    }

    saveTag(): angular.IPromise<boolean> {
        const p = this._storeService.dispatchers().tags.update([this.selectedTag]);
        return this._saveMessagesService
            .saveTag(p)
            // indicate form is not dirty anymore
            .then(tags => false);
    }

    private reselectTag(tag: TagDefinition) {
        this.selectedTag = tag;
        this.searchString = `t:${tag.name}`;
    }
}

export const wtTagsStatisticsState: ComponentDefinition = {
    componentId: 'wtTagsStatisticsState',
    controller: TagsStatisticsStateController,
    template: require('./tagsStatisticsState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};